import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { MenuItemDto } from '../../api-types';
import { ApiError } from '../../api/errors';
import {
    useDeleteMenuItemPhotoMutation,
    useUploadMenuItemPhotoMutation
} from '../../api/menuApi';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { clearError, selectError, setError } from '../../app/commonSlice';

import './index.css';
import { UseFormReturn } from 'react-hook-form';

interface IProps {
    form: UseFormReturn<MenuItemDto, unknown>;
    menuItem: MenuItemDto;
}

export function EditPhoto({ form, menuItem }: IProps) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { getValues, setValue } = form;

    const error = useAppSelector(selectError);
    const [uploadMenuItemPhoto] = useUploadMenuItemPhotoMutation();
    const [deleteMenuItemPhoto] = useDeleteMenuItemPhotoMutation();

    const handleResponse = useCallback(
        (response: MenuItemDto) => {
            dispatch(clearError());
            setValue('image', response.image);
        },
        [dispatch, setValue]
    );

    const handleUploadFile = useCallback(
        (file: FileList | null) => {
            if (file !== null) {
                const formData = new FormData();
                formData.append('image', file[0]);
                uploadMenuItemPhoto({ uuid: menuItem.uuid, formData })
                    .unwrap()
                    .then((response) => handleResponse(response))
                    .catch((error: unknown) => {
                        if (error instanceof ApiError) {
                            dispatch(setError(error));
                            setValue('image', null);
                        }
                    });
            }
        },
        [menuItem, uploadMenuItemPhoto, handleResponse, dispatch, setValue]
    );

    const handleDeleteImage = useCallback(() => {
        deleteMenuItemPhoto({ uuid: menuItem.uuid })
            .unwrap()
            .then((response) => handleResponse(response))
            .catch((error: unknown) => {
                if (error instanceof ApiError) {
                    dispatch(setError(error));
                    setValue('image', null);
                }
            });
    }, [menuItem, deleteMenuItemPhoto, handleResponse, dispatch, setValue]);

    return (
        <div className='photo'>
            <label>{t('photo')}</label>
            <div className='wrapper'>
                <img
                    src={getValues('image') ?? '/images/placeholder.png'}
                    alt='menu-item-photo'
                />
                <input
                    type='file'
                    hidden
                    id='photo'
                    onChange={(event) => handleUploadFile(event.target.files)}
                    value=''
                    accept='image/x-png,image/jpeg'
                />
                <div className='actions'>
                    <button
                        className='btn btn-white'
                        type='button'
                        onClick={() =>
                            document.getElementById('photo')?.click()
                        }
                    >
                        {t('upload_photo')}
                    </button>
                    <button
                        className='btn btn-white btn-danger'
                        type='button'
                        onClick={handleDeleteImage}
                    >
                        {t('remove_photo')}
                    </button>
                </div>
            </div>
            {error !== undefined && (
                <label className='error'>{error.message}</label>
            )}
        </div>
    );
}
